import {
  useState,
  useEffect,
  useCallback,
  useRef,
  createContext,
  useContext,
  ReactNode,
} from 'react';
import { createConsumer, Subscription } from '@rails/actioncable';
import { BackgroundJob } from '../types';
import { getGraphQLClient, GET_ACTIVE_BACKGROUND_JOBS, GET_BACKGROUND_JOB } from '../lib/graphql';

const STORAGE_KEY = 'background-jobs-state';

// Timeout settings (in milliseconds)
const JOB_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes without updates = stale
const STALE_CHECK_INTERVAL_MS = 30 * 1000; // Check every 30 seconds
const POLL_INTERVAL_MS = 15 * 1000; // Poll every 15 seconds as fallback

// Create a singleton consumer for ActionCable
let consumer: ReturnType<typeof createConsumer> | null = null;

function getConsumer() {
  if (!consumer) {
    consumer = createConsumer();
  }
  return consumer;
}

// Extended job type with last update tracking
interface TrackedJob extends BackgroundJob {
  lastUpdateAt: number; // timestamp
}

// Normalize job status from server (uppercase) to frontend format (lowercase)
function normalizeJobStatus(job: BackgroundJob): BackgroundJob {
  return {
    ...job,
    status: job.status.toLowerCase() as BackgroundJob['status'],
  };
}

// Add tracking info to job
function toTrackedJob(job: BackgroundJob): TrackedJob {
  return {
    ...job,
    lastUpdateAt: Date.now(),
  };
}

interface BackgroundJobsState {
  jobs: TrackedJob[];
  lastUpdated: string;
}

// Callback type for job completion notifications
type JobCompletionCallback = (job: BackgroundJob) => void;

interface UseBackgroundJobsResult {
  activeJobs: BackgroundJob[];
  isLoading: boolean;
  trackJob: (job: BackgroundJob) => void;
  removeJob: (jobId: string) => void;
  getJobForTarget: (
    targetType: string,
    targetId: string,
    jobType?: string,
    activeOnly?: boolean
  ) => BackgroundJob | undefined;
  refreshJobs: () => Promise<void>;
  subscribeToJobCompletion: (callback: JobCompletionCallback) => () => void;
}

// Load persisted state from localStorage
function loadPersistedState(): BackgroundJobsState | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const state = JSON.parse(stored) as BackgroundJobsState;
      // Check if state is less than 24 hours old
      const lastUpdated = new Date(state.lastUpdated);
      const now = new Date();
      const hoursDiff = (now.getTime() - lastUpdated.getTime()) / (1000 * 60 * 60);
      if (hoursDiff < 24) {
        return state;
      }
    }
  } catch {
    // Ignore errors
  }
  return null;
}

// Persist state to localStorage
function persistState(jobs: TrackedJob[]) {
  try {
    const state: BackgroundJobsState = {
      jobs: jobs.filter((j) => j.status === 'pending' || j.status === 'processing'),
      lastUpdated: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore errors
  }
}

// Check if a job is stale (no updates for too long)
function isJobStale(job: TrackedJob): boolean {
  if (job.status !== 'pending' && job.status !== 'processing') {
    return false;
  }
  const timeSinceUpdate = Date.now() - job.lastUpdateAt;
  return timeSinceUpdate > JOB_TIMEOUT_MS;
}

export function useBackgroundJobs(): UseBackgroundJobsResult {
  const [activeJobs, setActiveJobs] = useState<TrackedJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const subscriptionRef = useRef<Subscription | null>(null);
  const subscribedJobIdsRef = useRef<Set<string>>(new Set());
  const subscriptionsMapRef = useRef<Map<string, Subscription>>(new Map());
  const completionCallbacksRef = useRef<Set<JobCompletionCallback>>(new Set());

  // Fetch all active jobs from server
  const fetchActiveJobs = useCallback(async (): Promise<TrackedJob[]> => {
    try {
      const client = getGraphQLClient();
      const data = await client.request<{ activeBackgroundJobs: BackgroundJob[] }>(
        GET_ACTIVE_BACKGROUND_JOBS
      );
      return data.activeBackgroundJobs.map((j) => toTrackedJob(normalizeJobStatus(j)));
    } catch {
      return [];
    }
  }, []);

  // Unsubscribe from a specific job
  const unsubscribeFromJob = useCallback((jobId: string) => {
    const subscription = subscriptionsMapRef.current.get(jobId);
    if (subscription) {
      subscription.unsubscribe();
      subscriptionsMapRef.current.delete(jobId);
    }
    subscribedJobIdsRef.current.delete(jobId);
  }, []);

  // Helper to handle job completion (update state, unsubscribe, notify callbacks)
  const handleJobCompletion = useCallback((job: BackgroundJob, subscription?: Subscription) => {
    const trackedJob = toTrackedJob(normalizeJobStatus(job));
    const jobId = trackedJob.id;

    // Update state
    setActiveJobs((prev) => {
      const newJobs = prev.map((j) => (j.id === jobId ? trackedJob : j));
      if (!prev.find((j) => j.id === jobId)) {
        newJobs.push(trackedJob);
      }
      persistState(newJobs);
      return newJobs;
    });

    // Unsubscribe if job is complete or failed
    if (trackedJob.status === 'completed' || trackedJob.status === 'failed') {
      subscribedJobIdsRef.current.delete(jobId);
      if (subscription) {
        subscriptionsMapRef.current.delete(jobId);
        subscription.unsubscribe();
      } else {
        // Clean up subscription from map if it exists
        const existingSub = subscriptionsMapRef.current.get(jobId);
        if (existingSub) {
          existingSub.unsubscribe();
          subscriptionsMapRef.current.delete(jobId);
        }
      }

      // Notify all completion subscribers
      completionCallbacksRef.current.forEach((callback) => {
        try {
          callback(trackedJob);
        } catch (e) {
          console.error('Error in job completion callback:', e);
        }
      });
    }
  }, []);

  // Subscribe to a specific job's updates
  const subscribeToJob = useCallback((jobId: string) => {
    if (subscribedJobIdsRef.current.has(jobId)) {
      return; // Already subscribed
    }

    const cable = getConsumer();
    const query = `
      subscription BackgroundJobUpdated($jobId: ID) {
        backgroundJobUpdated(jobId: $jobId) {
          id
          jobType
          targetType
          targetId
          status
          progress
          errorMessage
          result
          partialResult
          startedAt
          estimatedDurationMs
          createdAt
          updatedAt
          targetInfo {
            bookTitle
            beatTitle
            beatNumber
          }
        }
      }
    `;

    const subscription = cable.subscriptions.create(
      { channel: 'GraphqlChannel' },
      {
        connected() {
          this.perform('execute', {
            query,
            variables: { jobId },
          });

          // After subscription is established, immediately check current job status
          // This catches jobs that completed before the subscription was ready
          setTimeout(async () => {
            // Skip if we've already unsubscribed (job completed via normal flow)
            if (!subscribedJobIdsRef.current.has(jobId)) {
              return;
            }

            try {
              const client = getGraphQLClient();
              const data = await client.request<{ backgroundJob: BackgroundJob | null }>(
                GET_BACKGROUND_JOB,
                { id: jobId }
              );

              if (data.backgroundJob) {
                const job = normalizeJobStatus(data.backgroundJob);
                if (job.status === 'completed' || job.status === 'failed') {
                  // Job already finished - trigger completion manually
                  handleJobCompletion(job, subscription);
                }
              }
            } catch (error) {
              console.error('Error checking job status after subscription:', error);
            }
          }, 100);
        },
        received(response: {
          result?: {
            data?: { backgroundJobUpdated?: BackgroundJob };
            errors?: Array<{ message: string }>;
          };
        }) {
          if (response.result?.data?.backgroundJobUpdated) {
            handleJobCompletion(response.result.data.backgroundJobUpdated, subscription);
          }
        },
      }
    );

    subscribedJobIdsRef.current.add(jobId);
    subscriptionsMapRef.current.set(jobId, subscription);
  }, [handleJobCompletion]);

  // Check for and handle stale jobs by polling server for actual status
  const removeStaleJobs = useCallback(async () => {
    const staleJobs = activeJobs.filter(isJobStale);
    if (staleJobs.length === 0) return;

    console.warn(`Found ${staleJobs.length} stale job(s), checking server status...`);

    const client = getGraphQLClient();
    for (const staleJob of staleJobs) {
      try {
        const data = await client.request<{ backgroundJob: BackgroundJob | null }>(
          GET_BACKGROUND_JOB,
          { id: staleJob.id }
        );

        if (data.backgroundJob) {
          const job = normalizeJobStatus(data.backgroundJob);
          if (job.status === 'completed' || job.status === 'failed') {
            // Job actually finished - trigger proper completion flow
            console.warn(`Stale job ${staleJob.id} already ${job.status} on server, triggering completion`);
            handleJobCompletion(job);
          } else {
            // Job still active on server - refresh timestamp to prevent re-checking
            console.warn(`Stale job ${staleJob.id} still ${job.status} on server, refreshing timestamp`);
            setActiveJobs((prev) =>
              prev.map((j) => j.id === staleJob.id ? { ...j, lastUpdateAt: Date.now() } : j)
            );
          }
        } else {
          // Job not found on server - remove it
          console.warn(`Stale job ${staleJob.id} not found on server, removing`);
          setActiveJobs((prev) => {
            const newJobs = prev.filter((j) => j.id !== staleJob.id);
            persistState(newJobs);
            return newJobs;
          });
          unsubscribeFromJob(staleJob.id);
        }
      } catch (error) {
        console.error(`Error checking stale job ${staleJob.id}:`, error);
      }
    }
  }, [activeJobs, handleJobCompletion, unsubscribeFromJob]);

  // Initialize from persisted state and server, then subscribe to active jobs
  useEffect(() => {
    const initialize = async () => {
      setIsLoading(true);

      // Load persisted state first
      const persisted = loadPersistedState();
      const persistedJobs = persisted?.jobs || [];

      // Then fetch from server
      const serverJobs = await fetchActiveJobs();

      // Merge: server data takes precedence
      const mergedJobs = [...serverJobs];
      persistedJobs.forEach((pj) => {
        if (!mergedJobs.find((sj) => sj.id === pj.id)) {
          // Only add persisted jobs that aren't stale
          if (!isJobStale(pj)) {
            mergedJobs.push(pj);
          }
        }
      });

      setActiveJobs(mergedJobs);
      persistState(mergedJobs);
      setIsLoading(false);

      // Subscribe to active jobs
      mergedJobs
        .filter((j) => j.status === 'pending' || j.status === 'processing')
        .forEach((j) => subscribeToJob(j.id));
    };

    initialize();

    return () => {
      // Cleanup subscriptions
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }
      // Cleanup all job subscriptions
      subscriptionsMapRef.current.forEach((sub) => sub.unsubscribe());
      subscriptionsMapRef.current.clear();
    };
  }, [fetchActiveJobs, subscribeToJob]);

  // Set up interval to check for stale jobs
  useEffect(() => {
    const interval = setInterval(removeStaleJobs, STALE_CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [removeStaleJobs]);

  // Polling fallback: periodically check active jobs in case WebSocket events are missed
  useEffect(() => {
    const pollActiveJobs = async () => {
      // Get jobs that are still tracked as active
      const trackedActive = activeJobs.filter(
        (j) => j.status === 'pending' || j.status === 'processing'
      );
      if (trackedActive.length === 0) return;

      try {
        // Check each tracked active job individually
        const client = getGraphQLClient();
        for (const trackedJob of trackedActive) {
          const data = await client.request<{ backgroundJob: BackgroundJob | null }>(
            GET_BACKGROUND_JOB,
            { id: trackedJob.id }
          );

          if (data.backgroundJob) {
            const job = normalizeJobStatus(data.backgroundJob);
            if (job.status === 'completed' || job.status === 'failed') {
              // Job finished but we missed the event - trigger completion
              handleJobCompletion(job);
            } else {
              // Job still active - refresh timestamp to prevent stale removal
              setActiveJobs((prev) =>
                prev.map((j) => j.id === trackedJob.id ? { ...j, lastUpdateAt: Date.now() } : j)
              );
            }
          }
        }
      } catch (error) {
        console.error('Error polling job status:', error);
      }
    };

    const interval = setInterval(pollActiveJobs, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [activeJobs, handleJobCompletion]);

  // Track a new job and subscribe to its updates
  const trackJob = useCallback(
    (job: BackgroundJob) => {
      const trackedJob = toTrackedJob(normalizeJobStatus(job));
      setActiveJobs((prev) => {
        const existing = prev.find((j) => j.id === trackedJob.id);
        if (existing) {
          return prev.map((j) => (j.id === trackedJob.id ? trackedJob : j));
        }
        const newJobs = [...prev, trackedJob];
        persistState(newJobs);
        return newJobs;
      });

      // Subscribe to updates for this job
      if (trackedJob.status === 'pending' || trackedJob.status === 'processing') {
        subscribeToJob(trackedJob.id);
      }
    },
    [subscribeToJob]
  );

  // Remove a job from tracking
  const removeJob = useCallback(
    (jobId: string) => {
      setActiveJobs((prev) => {
        const newJobs = prev.filter((j) => j.id !== jobId);
        persistState(newJobs);
        return newJobs;
      });
      unsubscribeFromJob(jobId);
    },
    [unsubscribeFromJob]
  );

  // Get job for a specific target (optionally filtered by job type)
  // By default returns only active (pending/processing) jobs. Set activeOnly=false to include completed/failed.
  const getJobForTarget = useCallback(
    (
      targetType: string,
      targetId: string,
      jobType?: string,
      activeOnly: boolean = true
    ): BackgroundJob | undefined => {
      return activeJobs.find(
        (j) =>
          j.targetType === targetType &&
          j.targetId === targetId &&
          (!activeOnly || j.status === 'pending' || j.status === 'processing') &&
          (jobType ? j.jobType === jobType : true)
      );
    },
    [activeJobs]
  );

  // Refresh all jobs from server
  const refreshJobs = useCallback(async () => {
    const serverJobs = await fetchActiveJobs();
    setActiveJobs((prev) => {
      // Keep completed/failed jobs from prev that aren't on server
      const completedOrFailed = prev.filter(
        (j) =>
          (j.status === 'completed' || j.status === 'failed') &&
          !serverJobs.find((sj) => sj.id === j.id)
      );
      const newJobs = [...serverJobs, ...completedOrFailed];
      persistState(newJobs);

      // Subscribe to new active jobs
      serverJobs
        .filter((j) => j.status === 'pending' || j.status === 'processing')
        .forEach((j) => subscribeToJob(j.id));

      return newJobs;
    });
  }, [fetchActiveJobs, subscribeToJob]);

  // Subscribe to job completion notifications
  const subscribeToJobCompletion = useCallback((callback: JobCompletionCallback) => {
    completionCallbacksRef.current.add(callback);
    // Return unsubscribe function
    return () => {
      completionCallbacksRef.current.delete(callback);
    };
  }, []);

  return {
    activeJobs,
    isLoading,
    trackJob,
    removeJob,
    getJobForTarget,
    refreshJobs,
    subscribeToJobCompletion,
  };
}

// Context for sharing background jobs state across components
const BackgroundJobsContext = createContext<UseBackgroundJobsResult | null>(null);

export function BackgroundJobsProvider({ children }: { children: ReactNode }) {
  const backgroundJobs = useBackgroundJobs();

  return (
    <BackgroundJobsContext.Provider value={backgroundJobs}>
      {children}
    </BackgroundJobsContext.Provider>
  );
}

export function useBackgroundJobsContext(): UseBackgroundJobsResult {
  const context = useContext(BackgroundJobsContext);
  if (!context) {
    throw new Error('useBackgroundJobsContext must be used within a BackgroundJobsProvider');
  }
  return context;
}
